import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Col, Row } from '../../lib/utils';
import { Body2Bold, Body3Regular, Body3Bold, CaptionRegular } from '../../lib/font';
import { colors } from '../../lib/colors';
import { getPaymentRequest, formatPrice } from './data/dummyData';
import PaymentHeader from './components/PaymentHeader';
import AgreementCheckbox from './components/AgreementCheckbox';
import StickyBottomButton from './components/StickyBottomButton';

const MemberPaymentCardRegPage: React.FC = () => {
    const { paymentRequestId } = useParams<{ paymentRequestId: string }>();
    const navigate = useNavigate();
    const [isAutoPayAgreed, setIsAutoPayAgreed] = useState(false);

    const data = getPaymentRequest(paymentRequestId || '');

    if (!data) {
        return (
            <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', justifyContent: 'center', backgroundColor: '#F5F7FA' }}>
                <Body2Bold style={{ color: colors.ERROR }}>요청을 찾을 수 없습니다</Body2Bold>
            </Col>
        );
    }

    const handleCardRegister = () => {
        if (!isAutoPayAgreed) return;
        navigate(`/pay/${paymentRequestId}/mock-pg?type=billing`);
    };

    return (
        <Col style={{ width: '100%', minHeight: '100vh', alignItems: 'center', backgroundColor: '#F5F7FA' }}>
            <Col style={{ width: '100%', maxWidth: 480, minHeight: '100vh', backgroundColor: '#F5F7FA' }}>
                <PaymentHeader title="카드 등록" />

                <Col style={{ flex: 1, padding: '16px', paddingBottom: 80, gap: 12 }}>
                    {/* 안내 배너 */}
                    <Col
                        style={{
                            width: '100%',
                            padding: '20px',
                            backgroundColor: '#EFF6FF',
                            borderRadius: 16,
                            alignItems: 'center',
                            gap: 8,
                        }}
                    >
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: 24,
                                backgroundColor: colors.primary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <rect x="2" y="5" width="20" height="14" rx="3" stroke="#FFFFFF" strokeWidth="2" />
                                <path d="M2 10h20" stroke="#FFFFFF" strokeWidth="2" />
                            </svg>
                        </div>
                        <Body2Bold style={{ color: '#1E40AF', textAlign: 'center' }}>
                            수업이 완료될 때마다 등록하신 카드로 자동 결제됩니다
                        </Body2Bold>
                        <CaptionRegular style={{ color: '#3B82F6', textAlign: 'center', lineHeight: '18px' }}>
                            지금은 카드 정보만 등록하며, 수업료는 수업이 끝난 뒤에 청구됩니다
                        </CaptionRegular>
                    </Col>

                    {/* 자동결제 구조 안내 */}
                    <Col
                        style={{
                            width: '100%',
                            backgroundColor: '#FFFFFF',
                            borderRadius: 16,
                            padding: '24px 20px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        }}
                    >
                        <Body3Bold style={{ color: colors.LAVEL_4, marginBottom: 16 }}>자동결제 안내</Body3Bold>

                        <Row style={{ justifyContent: 'space-between', padding: '10px 0' }}>
                            <CaptionRegular style={{ color: colors.LAVEL_2 }}>수강권명</CaptionRegular>
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>{data.ticketName}</Body3Regular>
                        </Row>
                        <Row style={{ justifyContent: 'space-between', padding: '10px 0' }}>
                            <CaptionRegular style={{ color: colors.LAVEL_2 }}>1회 수업료</CaptionRegular>
                            <Body3Regular style={{ color: colors.primary, fontWeight: 600 }}>
                                {formatPrice(data.perSessionPrice)}원
                            </Body3Regular>
                        </Row>
                        <Row style={{ justifyContent: 'space-between', padding: '10px 0' }}>
                            <CaptionRegular style={{ color: colors.LAVEL_2 }}>결제 시점</CaptionRegular>
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>각 수업 종료 후 자동</Body3Regular>
                        </Row>
                        <Row style={{ justifyContent: 'space-between', padding: '10px 0' }}>
                            <CaptionRegular style={{ color: colors.LAVEL_2 }}>담당 트레이너</CaptionRegular>
                            <Body3Regular style={{ color: colors.LAVEL_4 }}>{data.trainerName}</Body3Regular>
                        </Row>
                    </Col>

                    {/* 유의사항 */}
                    <Col
                        style={{
                            width: '100%',
                            backgroundColor: '#FFFFFF',
                            borderRadius: 16,
                            padding: '24px 20px',
                            boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                        }}
                    >
                        <Body3Bold style={{ color: colors.LAVEL_4, marginBottom: 12 }}>유의사항</Body3Bold>
                        <CaptionRegular style={{ color: colors.LAVEL_3, lineHeight: '20px' }}>
                            · 카드 등록 시 실제 결제는 발생하지 않습니다
                        </CaptionRegular>
                        <CaptionRegular style={{ color: colors.LAVEL_3, lineHeight: '20px', marginTop: 6 }}>
                            · 수업 취소 시 해당 수업료는 청구되지 않습니다
                        </CaptionRegular>
                        <CaptionRegular style={{ color: colors.LAVEL_3, lineHeight: '20px', marginTop: 6 }}>
                            · 등록된 카드는 센터 문의를 통해 변경할 수 있습니다
                        </CaptionRegular>
                    </Col>

                    {/* 자동결제 동의 */}
                    <AgreementCheckbox
                        checked={isAutoPayAgreed}
                        onChange={setIsAutoPayAgreed}
                        label="수업 종료 후 자동 결제에 동의합니다"
                    />
                </Col>

                <StickyBottomButton
                    text="카드 등록하기"
                    onClick={handleCardRegister}
                    disabled={!isAutoPayAgreed}
                />
            </Col>
        </Col>
    );
};

export default MemberPaymentCardRegPage;
